import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Copy, Check, Gift, Users, TrendingUp, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { DownloadMdButton } from "@/components/DownloadMdButton";
import { buildReferralsMd } from "@/lib/mdBuilders";
export default function Referrals() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        client.get("/referrals/me").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="text-muted-foreground font-mono text-sm">loading…</div>;

    const url = `${window.location.origin}/?ref=${data.referral_code}`;
    const copy = async () => {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
        toast.success("Referral link copied");
    };

    return (
        <div className="space-y-6" data-testid="referrals-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline">Referral program</div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Refer & Earn Pro months.</h1>
                    <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                        Every public share dashboard you send to a client carries a "Powered by InPhase" link tagged with your code. Every signup earns you 1 free month · every upgrade earns you a bonus month. Credits auto-apply at checkout.
                    </p>
                </div>
                <DownloadMdButton
                    filenamePrefix="inphase-referrals"
                    buildMarkdown={() => buildReferralsMd({ data })}
                    testId="download-referrals-md"
                />
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-primary/60 bg-primary/5 p-6" data-testid="stat-credits">
                    <div className="flex items-center gap-2 mb-2">
                        <Gift className="w-4 h-4 text-primary" />
                        <div className="overline text-primary">Free months banked</div>
                    </div>
                    <div className="font-mono text-4xl text-primary">{data.credit_months}</div>
                    <div className="text-xs text-muted-foreground mt-2">= {data.credit_months * 30} trial days · auto-applied at Pro checkout</div>
                </div>
                <div className="border border-border bg-card/40 p-6" data-testid="stat-signups">
                    <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <div className="overline">Signups from you</div>
                    </div>
                    <div className="font-mono text-4xl">{data.referred_signups}</div>
                </div>
                <div className="border border-border bg-card/40 p-6" data-testid="stat-upgraded">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-muted-foreground" />
                        <div className="overline">Upgraded to Pro</div>
                    </div>
                    <div className="font-mono text-4xl">{data.referred_upgraded}</div>
                </div>
            </div>

            {/* Share link box */}
            <div className="border border-border bg-card/40 p-8">
                <div className="overline mb-3">Your referral link</div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-0 bg-input border border-border px-4 py-3 font-mono text-sm truncate" data-testid="referral-url">
                        {url}
                    </div>
                    <button
                        onClick={copy}
                        data-testid="copy-referral-btn"
                        className="bg-primary text-primary-foreground px-4 py-3 text-sm font-medium hover:bg-primary/90 flex items-center gap-2"
                    >
                        {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy link</>}
                    </button>
                </div>
                <div className="mt-4 text-xs text-muted-foreground">
                    Also automatically embedded in every public share dashboard footer as "Powered by InPhase".
                </div>
            </div>

            {/* Redeem CTA */}
            {data.credit_months > 0 && user?.tier !== "pro" && (
                <div className="border border-primary/60 bg-primary/5 p-6 flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="font-display text-xl">
                            {data.credit_months} free {data.credit_months === 1 ? "month" : "months"} waiting.
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">Upgrade to Pro — your credits auto-apply as a trial period. Zero risk.</div>
                    </div>
                    <Link to="/pricing" data-testid="redeem-link"
                          className="bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-primary/90 flex items-center gap-2">
                        Redeem credits <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            )}

            {/* Event log */}
            <div className="border border-border bg-card/40">
                <div className="p-4 border-b border-border">
                    <div className="overline">Recent referral events</div>
                </div>
                {data.events.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">No referrals yet. Share your link on Twitter, in your newsletter, or embed it in a client dashboard.</div>
                ) : (
                    <div className="divide-y divide-border/50">
                        {[...data.events].reverse().map((ev, i) => (
                            <div key={i} className="p-4 flex items-center gap-4">
                                <div className={`w-8 h-8 border grid place-items-center ${ev.type === "upgrade" ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                                    {ev.type === "upgrade" ? <TrendingUp className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                                </div>
                                <div className="flex-1 text-sm">
                                    {ev.type === "upgrade" ? "Referred user upgraded to Pro" : "New signup via your link"}
                                    <div className="text-xs text-muted-foreground font-mono">{ev.referred_user_id?.slice(0, 8) || "—"}</div>
                                </div>
                                <div className="text-xs font-mono text-muted-foreground">
                                    {new Date(ev.at).toLocaleDateString()}
                                </div>
                                <div className="text-xs text-primary font-mono">+1 month</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
